import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { 
  Plane, 
  MapPin, 
  Calendar, 
  Ticket, 
  MessageSquare, 
  Sun, 
  Moon, 
  Menu, 
  X,
  ArrowRight,
  Star
} from "lucide-react";

const LandingPage = () => {
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Pianifica Destinazioni",
      description: "Organizza i tuoi viaggi con destinazioni, date e itinerari dettagliati"
    },
    {
      icon: <Ticket className="w-6 h-6" />,
      title: "Gestisci Prenotazioni",
      description: "Conserva tutti i tuoi biglietti e prenotazioni in un unico posto"
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Crea Itinerari",
      description: "Pianifica giorno per giorno le tue attività e visite"
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Assistente AI",
      description: "Ottieni consigli personalizzati sui migliori siti di prenotazione"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <Plane className="w-7 h-7 text-primary" />
              <span className="font-heading text-xl font-bold">TravelPro</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-contact">
                Contatti
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>
              {isAuthenticated ? (
                <Button onClick={() => navigate("/dashboard")} data-testid="nav-dashboard">
                  Dashboard
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login">
                    Accedi
                  </Button>
                  <Button onClick={() => navigate("/register")} className="rounded-full" data-testid="nav-register">
                    Registrati
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-mobile">
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl">
            <div className="px-4 py-4 space-y-3">
              <Link to="/contact" className="block py-2 text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>
                Contatti
              </Link>
              {isAuthenticated ? (
                <Button className="w-full" onClick={() => { navigate("/dashboard"); setMobileMenuOpen(false); }}>
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button variant="outline" className="w-full" onClick={() => { navigate("/login"); setMobileMenuOpen(false); }}>
                    Accedi
                  </Button>
                  <Button className="w-full" onClick={() => { navigate("/register"); setMobileMenuOpen(false); }}>
                    Registrati
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeIn">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-sm mb-6">
                <Star className="w-4 h-4 text-accent" />
                <span>La tua prossima avventura inizia qui</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Pianifica i tuoi viaggi con{" "}
                <span className="text-primary">semplicità</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                Organizza destinazioni, itinerari e prenotazioni in un unico posto. 
                Con l'aiuto dell'intelligenza artificiale per consigli personalizzati.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="rounded-full text-base px-8"
                  onClick={() => navigate(isAuthenticated ? "/dashboard" : "/register")}
                  data-testid="cta-start"
                >
                  Inizia ora
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full text-base px-8"
                  onClick={() => navigate("/contact")}
                  data-testid="cta-contact"
                >
                  Scopri di più
                </Button>
              </div>
            </div>

            <div className="relative animate-slideUp" style={{ animationDelay: "0.2s" }}>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.15)]">
                <img 
                  src="https://images.unsplash.com/photo-1636815529433-d1cd851d5d50?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Travel planning"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-card p-4 rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.12)] border border-border hidden sm:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Plane className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-mono text-xs text-muted-foreground">Prossimo viaggio</p>
                    <p className="font-semibold">Roma → Tokyo</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Tutto ciò di cui hai bisogno
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Un'applicazione completa per organizzare ogni aspetto del tuo viaggio
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-card p-6 rounded-xl border border-border hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)] transition-all duration-300 animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 text-primary">
                  {feature.icon}
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
            Pronto a partire?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Inizia a pianificare il tuo prossimo viaggio oggi stesso. 
            Registrati gratuitamente e scopri tutte le funzionalità.
          </p>
          <Button 
            size="lg" 
            className="rounded-full text-base px-10"
            onClick={() => navigate(isAuthenticated ? "/dashboard" : "/register")}
            data-testid="cta-bottom"
          >
            {isAuthenticated ? "Vai alla Dashboard" : "Crea account gratuito"}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Plane className="w-5 h-5 text-primary" />
            <span className="font-heading font-semibold">TravelPro</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2024 TravelPro. Tutti i diritti riservati.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Contatti
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
